<template>
  <a-layout style="min-height: 100vh">
    <a-layout-sider
      v-model:collapsed="state.collapsed"
      collapsible
      :theme="theme"
      class="layout-sider"
      @breakpoint="onBreakpoint"
    >
      <div :class="['logoWrap', theme === 'light' ? 'light' : 'dark']">
        <div class="logo">
          <slot name="logo"><img src="@/assets/logo.png" alt="" /></slot>
        </div>
        <div class="text">雷能信息后台管理</div>
      </div>
      <SideMenu :collapsed="collapsed" />
    </a-layout-sider>

    <a-layout class="layout-content">
      <a-layout-header>
        <slot name="header">
          <!-- <iframe
            scrolling="no"
            src="https://widget.tianqiapi.com/?style=tv&skin=pear&color=ffffff&fontsize=14"
            frameborder="0"
            width="130"
            height="20"
            allowtransparency="true"
          ></iframe> -->
          <BaseWeather size="small" :iconSize="24" showPanel />

          <div class="smartScreen" @click="openSmartScreen">智慧大屏</div>

          <time-now class="timeNow"></time-now>
          <SyncOutlined class="refresh" @click="refresh" />
          <user-dropdown></user-dropdown>
        </slot>
      </a-layout-header>
      <a-layout-content>
        <slot name="breadcrumb">
          <div class="tabs-container" ref="tabsContainerRef">
            <a-tabs
              v-model:activeKey="breadcrumbActiveKey"
              hide-add
              type="editable-card"
              size="small"
              @change="breadcrumbChangeHandler"
              @edit="breadcrumbDeleteHandler"
            >
              <a-tab-pane
                v-for="pane in breadcrumbs"
                :key="pane.path"
                :tab="pane.name"
                :closable="breadcrumbs.length !== 1"
                :class="['tab-pane-item', { active: pane.path === breadcrumbActiveKey }]"
              >
              </a-tab-pane>
            </a-tabs>

            <!-- 右键菜单 -->
            <div
              v-if="contextMenuVisible"
              class="context-menu-container"
              :style="{ left: contextMenuLeft + 'px', top: contextMenuTop + 'px' }"
              @click.stop
            >
              <a-menu class="context-menu">
                <a-menu-item key="closeLeft" :disabled="!canCloseLeft" @click="closeLeftHandler"
                  >关闭左侧</a-menu-item
                >
                <a-menu-item key="closeRight" :disabled="!canCloseRight" @click="closeRightHandler"
                  >关闭右侧</a-menu-item
                >
                <a-menu-item key="closeOther" :disabled="!canCloseOther" @click="closeOtherHandler"
                  >关闭其他</a-menu-item
                >
              </a-menu>
            </div>
          </div>
        </slot>
        <slot></slot>
      </a-layout-content>
      <a-layout-footer>
        <slot name="footer">
          <Copyright
            company="合肥雷能信息技术有限公司"
            :version="version"
            :icp="icpText"
            icp-link="https://beian.miit.gov.cn"
            :icp-text="icpText"
            font-color="#4774a7"
          />
        </slot>
      </a-layout-footer>
    </a-layout>

    <alert-modal v-model:open="openAlertModal" :data="alertModalState"></alert-modal>
  </a-layout>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onUnmounted, onMounted, nextTick, watchEffect } from 'vue'
import Sortable, { type SortableEvent } from 'sortablejs'
import userDropdown from './components/userDropdown/index.vue'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'
import timeNow from './components/timeNow/index.vue'
import mqtt, { type MqttClient } from 'mqtt'
import { useUserStore } from '@/stores/user'
import AlertModal from './components/alertModal/index.vue'
import { SyncOutlined } from '@ant-design/icons-vue'
import SideMenu from './components/sideMenu/index.vue'
import { useBreadcrumbStore } from '@/stores/breadcrumb'
import { formatDateTime } from '@/utils'
import { isUeScreenTenantCode } from '@/views/screen/shared/constants'
const breadcrumbStore = useBreadcrumbStore()

const breadcrumbs = computed(() => breadcrumbStore.getRoutes())

// 右键菜单相关
const contextMenuVisible = ref(false)
const contextMenuLeft = ref(0)
const contextMenuTop = ref(0)

interface RightClickedTab {
  pane: {
    path: string
    name: string
    query?: LocationQueryRaw
  }
  index: number
}

const currentRightClickedTab = ref<RightClickedTab | null>(null)

// 判断菜单项是否应该禁用
const canCloseLeft = computed(() => {
  if (!currentRightClickedTab.value) return false
  const index = currentRightClickedTab.value.index
  return index > 0
})

const canCloseRight = computed(() => {
  if (!currentRightClickedTab.value) return false
  const index = currentRightClickedTab.value.index
  return index !== -1 && index < breadcrumbs.value.length - 1
})

const canCloseOther = computed(() => {
  return breadcrumbs.value.length > 1
})

// 拖拽相关
const tabsContainerRef = ref<HTMLElement | null>(null)
let sortable: Sortable | null = null

const userStore = useUserStore()
const userId = ref(userStore?.userInfo?.userId || '')
const version = __APP_VERSION__

const mode = import.meta.env.MODE

const icpTextDev = ''
const icpTextCn = '皖ICP备2024060056号'
const icpTextCom = '皖ICP备2024060056号'
const icpText = computed(() => {
  if (mode === 'production') {
    return icpTextCom
  } else if (mode === 'test') {
    return icpTextCn
  }
  return icpTextDev
})

const emit = defineEmits(['refresh'])
// 刷新
const refresh = () => {
  emit('refresh')
}

const collapsed = ref<boolean>(false)

const onBreakpoint = (broken: boolean) => {
  if (broken) {
    collapsed.value = true
  }
}

let mqttClient: MqttClient | null = null

const resetMqttTimeout = () => {
  console.log('🚀关闭MQTT连接')
  closeMqtt()
}

const MqttData = ref()

const initMqttManager = async () => {
  try {
    const mqttConfig = {
      host: import.meta.env.VITE_MQTT_HOST_ALARM,
      username: import.meta.env.VITE_MQTT_USERNAME,
      password: import.meta.env.VITE_MQTT_PASSWORD,
      clientId: `web_mqtt_cmd${Math.random().toString(16).slice(2)}`,
    }
    mqttClient = mqtt.connect(mqttConfig.host, {
      clientId: mqttConfig.clientId,
      username: mqttConfig.username,
      password: mqttConfig.password,
      will: {
        // ✅ 添加遗嘱消息配置
        topic: '/mps/client/connect',
        payload: JSON.stringify({
          userId: userId.value,
          deviceType: 'wb',
          msgType: 'disconnect',
        }),
        qos: 2,
        retain: false,
      },
    })
    mqttClient.on('connect', () => {
      console.log('MQTT已连接')
      const messageData = {
        userId: userId.value, // 用户ID
        deviceType: 'wb', // 设备类型
        msgType: 'connect', // 消息类型
      }
      // 发布消息
      mqttClient?.publish('/mps/client/connect', JSON.stringify(messageData), { qos: 2 }, (err) => {
        if (err) console.error('发布失败', err)
        // else console.log('✅ 已发送参数:', messageData)
      })
      // 订阅所有主题
      mqttClient?.subscribe(`/mps/client/connect/`, { qos: 2 }, (err) => {
        if (err) {
          console.error(`【MQTT】${formatDateTime(new Date())}，❌ MQTT订阅失败`, err)
        } else {
          console.log(`【MQTT】${formatDateTime(new Date())}，🔥已订阅主题 /mps/client/connect`)
        }
      })
      mqttClient?.subscribe(`/mps/wb_${userStore.userInfo?.userId}/notice`, { qos: 2 }, (err) => {
        if (err) {
          console.error('MQTT订阅失败', err)
        } else {
          console.log(
            `【MQTT】${formatDateTime(new Date())}，🔥已订阅主题 /mps/wb_${userStore.userInfo?.userId}/notice`
          )
        }
      })
    })
    mqttClient.on('error', (err) => {
      console.error(`【MQTT】${formatDateTime(new Date())}，❌ MQTT连接错误`, err)
    })
    mqttClient.on('message', (topic: string, message: Uint8Array) => {
      // resetMqttTimeout()
      try {
        const data = JSON.parse(message.toString())
        console.log(`【MQTT】${formatDateTime(new Date())}，👏👏收到MQTT消息`, topic, data)
        MqttData.value = data
      } catch (e) {
        console.error(`【MQTT】${formatDateTime(new Date())}，❌ MQTT消息解析失败`, e)
      }
    })
  } catch (error) {
    console.error(`【MQTT】${formatDateTime(new Date())}，❌ MQTT连接失败`, error)
  }
}

initMqttManager()

// 报警弹窗
const openAlertModal = ref(false)
const alertModalState = reactive({
  devName: '',
  tenantName: '',
  clientId: '',
  eventListId: '',
})
watchEffect(() => {
  const { msgType, event, devName, tenantName, clientId, eventListId } = MqttData.value || {}
  if (msgType === 'fall' && event === 'fall_confirmed') {
    alertModalState.devName = devName
    alertModalState.tenantName = tenantName
    alertModalState.clientId = clientId
    alertModalState.eventListId = eventListId
    openAlertModal.value = true
  }

  // 退出连接，取消mqtt连接
  if (!userStore.userInfo.tokenValue) {
    console.log(`【MQTT】${formatDateTime(new Date())}，🚀🚀🚀退出连接，取消mqtt连接`)
    closeMqtt()
  }
})

const closeMqtt = () => {
  mqttClient?.end(true)
}

onUnmounted(() => {
  resetMqttTimeout()
})

defineOptions({
  name: 'baseLayout',
})

const route = useRoute()
const router = useRouter()
const theme = ref('dark') // 主题 dark / light

const state = reactive({
  collapsed: false,
  selectedKeys: [route.name],
})

const routes = computed(() => {
  const currentRoute = router.currentRoute.value
  const items = []

  if (currentRoute.matched) {
    currentRoute.matched.forEach((matchedRoute) => {
      if (matchedRoute.meta?.title) {
        items.push({
          breadcrumbName: matchedRoute.meta.title,
          path: matchedRoute.path,
        })
      }
    })
  }

  if (items.length === 0 && currentRoute.meta?.title) {
    items.push({
      breadcrumbName: currentRoute.meta.title,
      path: currentRoute.path,
    })
  }

  return items
})

const clickedMenuItem = ref('')
watchEffect(() => {
  state.selectedKeys = [clickedMenuItem.value]
})

const canGoBack = ref(false)
const navigationHistory = ref<string[]>([])
let isBackNavigation = false

onMounted(() => {
  // 初始化记录当前完整路径（包含参数）
  navigationHistory.value = [router.currentRoute.value.fullPath]
  canGoBack.value = false
})

watchEffect(() => {
  const currentFullPath = router.currentRoute.value.fullPath

  if (isBackNavigation) {
    // 返回导航时不记录
    isBackNavigation = false
  } else {
    // 只记录新的路径（避免重复）
    if (navigationHistory.value[navigationHistory.value.length - 1] !== currentFullPath) {
      navigationHistory.value.push(currentFullPath)
    }
  }

  // 是否可以返回
  canGoBack.value = navigationHistory.value.length > 1
})

// 打开智慧大屏：团安 / 五里墩本级 → UE4；其余 → 高德
const openSmartScreen = () => {
  if (userStore.userInfo.tenantId) {
    if (isUeScreenTenantCode(userStore.userInfo.tenantCode)) {
      window.open('/screen/tuanan', '_blank')
      return
    }
    window.open('/screen/wulidun', '_blank')
    return
  }
  window.open('/screen/wulidun', '_blank')
}

const breadcrumbActiveKey = computed<string>(() => routes.value[1]?.path || '')

const breadcrumbChangeHandler = (key: string) => {
  router.push({
    path: key,
    query: breadcrumbStore.getRoutes().find((item) => item.path === key)?.query || {},
  })
}

const handleTabContextMenu = (
  event: MouseEvent,
  pane: { path: string; name: string; query?: LocationQueryRaw },
  index: number
) => {
  event.preventDefault()
  event.stopPropagation()

  currentRightClickedTab.value = { pane, index }
  contextMenuLeft.value = event.clientX
  contextMenuTop.value = event.clientY
  contextMenuVisible.value = true
}

// 切换到指定的 tab
const switchToTab = (path: string, query?: LocationQueryRaw) => {
  router.push({
    path,
    query: query || {},
  })
}

const closeLeftHandler = () => {
  if (!currentRightClickedTab.value || !canCloseLeft.value) return

  const currentIndex = currentRightClickedTab.value.index
  const currentPane = currentRightClickedTab.value.pane
  const activeKey = breadcrumbActiveKey.value

  // 检查当前激活的 tab 是否在要关闭的范围内
  const activeTabIndex = breadcrumbs.value.findIndex((item) => item.path === activeKey)
  const willCloseActiveTab = activeTabIndex !== -1 && activeTabIndex < currentIndex

  // 如果要关闭当前激活的 tab，先切换到右键点击的 tab
  if (willCloseActiveTab) {
    switchToTab(currentPane.path, currentPane.query)
  }

  const newBreadcrumbs = breadcrumbs.value.slice(currentIndex).map((item) => ({
    ...item,
    fullPath: item.path,
  }))
  breadcrumbStore.updateRoutes(newBreadcrumbs)
  contextMenuVisible.value = false
}

const closeRightHandler = () => {
  if (!currentRightClickedTab.value || !canCloseRight.value) return

  const currentIndex = currentRightClickedTab.value.index
  const currentPane = currentRightClickedTab.value.pane
  const activeKey = breadcrumbActiveKey.value

  // 检查当前激活的 tab 是否在要关闭的范围内
  const activeTabIndex = breadcrumbs.value.findIndex((item) => item.path === activeKey)
  const willCloseActiveTab = activeTabIndex !== -1 && activeTabIndex > currentIndex

  // 如果要关闭当前激活的 tab，先切换到右键点击的 tab
  if (willCloseActiveTab) {
    switchToTab(currentPane.path, currentPane.query)
  }

  const newBreadcrumbs = breadcrumbs.value.slice(0, currentIndex + 1).map((item) => ({
    ...item,
    fullPath: item.path,
  }))
  breadcrumbStore.updateRoutes(newBreadcrumbs)
  contextMenuVisible.value = false
}

const closeOtherHandler = () => {
  if (!currentRightClickedTab.value || !canCloseOther.value) return

  const currentPane = currentRightClickedTab.value.pane
  if (!currentPane) return

  // 切换到右键点击的 tab
  switchToTab(currentPane.path, currentPane.query)

  const newBreadcrumbs = [
    {
      ...currentPane,
      fullPath: currentPane.path,
    },
  ]
  breadcrumbStore.updateRoutes(newBreadcrumbs)
  contextMenuVisible.value = false
}

// 初始化拖拽排序和右键菜单事件
const initSortable = async () => {
  await nextTick()
  if (!tabsContainerRef.value) return

  const tabNav = tabsContainerRef.value.querySelector('.ant-tabs-nav-list')
  if (!tabNav) return

  // 初始化拖拽排序
  sortable = Sortable.create(tabNav, {
    animation: 150,
    handle: '.ant-tabs-tab',
    ghostClass: 'tab-drag-ghost',
    chosenClass: 'tab-drag-chosen',
    dragClass: 'tab-drag-drag',
    onStart: () => {
      document.body.classList.add('dragging-tabs')
    },
    onEnd: (evt: SortableEvent) => {
      document.body.classList.remove('dragging-tabs')
      const { oldIndex, newIndex } = evt
      if (oldIndex === null || newIndex === null) return

      const newBreadcrumbs = [...breadcrumbs.value]
      const [movedItem] = newBreadcrumbs.splice(oldIndex, 1)
      newBreadcrumbs.splice(newIndex, 0, movedItem)

      breadcrumbStore.updateRoutes(newBreadcrumbs)
    },
  })

  // 为所有标签页添加右键点击事件监听器
  const tabItems = tabsContainerRef.value.querySelectorAll('.ant-tabs-tab')
  tabItems.forEach((tabItem, index) => {
    // 先移除可能存在的事件监听器，避免重复绑定
    const existingHandler = (event: Event) => {
      const mouseEvent = event as MouseEvent
      const pane = breadcrumbs.value[index]
      if (pane) {
        handleTabContextMenu(mouseEvent, pane, index)
      }
    }

    // 使用类型断言处理事件监听器的类型匹配
    const eventType = 'contextmenu' as keyof HTMLElementEventMap
    tabItem.removeEventListener(eventType, existingHandler as EventListener)
    tabItem.addEventListener(eventType, existingHandler as EventListener)
  })
}

// 监听面包屑变化，重新初始化拖拽
watch(
  () => breadcrumbs.value,
  () => {
    nextTick(() => {
      if (sortable) {
        sortable.destroy()
      }
      initSortable()
    })
  },
  { deep: true }
)

// 点击页面其他地方关闭右键菜单
const handleClickOutside = () => {
  contextMenuVisible.value = false
}

// 页面加载完成后初始化拖拽
onMounted(() => {
  initSortable()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  if (sortable) {
    sortable.destroy()
  }
  document.removeEventListener('click', handleClickOutside)
})

const breadcrumbDeleteHandler = (key: string) => {
  const index = breadcrumbs.value.findIndex((item) => item.path === key)
  if (index === -1 || breadcrumbs.value.length <= 1) return

  // 检查是否删除的是当前激活的 tab
  const isActiveTab = key === breadcrumbActiveKey.value
  const newBreadcrumbs = [...breadcrumbs.value]
  newBreadcrumbs.splice(index, 1)

  let targetItem
  if (isActiveTab) {
    // 如果删除的是激活的 tab
    if (index < newBreadcrumbs.length) {
      // 如果不是最后一个 tab，激活下一个 tab
      targetItem = newBreadcrumbs[index]
    } else if (index > 0) {
      // 如果是最后一个 tab，激活前一个 tab
      targetItem = newBreadcrumbs[index - 1]
    } else {
      // 如果只有一个 tab，激活剩下的那个
      targetItem = newBreadcrumbs[0]
    }
  } else {
    // 如果删除的不是激活的 tab，保持激活 tab 不变
    targetItem =
      newBreadcrumbs.find((item) => item.path === breadcrumbActiveKey.value) ||
      newBreadcrumbs[index > 0 ? index - 1 : 0]
  }

  breadcrumbStore.updateRoutes(newBreadcrumbs)

  if (targetItem && targetItem.path) {
    // 如果删除的是激活的 tab，才需要跳转
    if (isActiveTab) {
      router.push({
        path: targetItem.path,
        query: targetItem.query,
      })
    }
  }
}
</script>

<style scoped lang="less">
.layout-sider {
  position: relative;
  background-color: #1a3a5f;

  :deep(.ant-layout-sider-children) {
    position: sticky;
    top: 0;
    height: auto;
    background-color: #1a3a5f;
    .ant-menu {
      background-color: #1a3a5f;
    }
  }
  :deep(.ant-layout-sider-trigger) {
    background-color: #1a3a5f;
  }
}

.logoWrap {
  height: 32px;
  margin: 16px;
  display: flex;
  align-items: center;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  color: #fff;

  .logo {
    width: 45px;
    height: 32px;
    flex-shrink: 0;
    font-size: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .text {
    margin-left: 10px;
    flex-grow: 1;
    opacity: 1;
    transition:
      opacity 0.3s cubic-bezier(0.645, 0.045, 0.355, 1),
      width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}

.light {
  color: #141414;
}
.dark {
  color: #fff;
}

.layout-content {
  .ant-layout-header {
    background-color: #1a3a5f;
    color: #fff;
    padding: 0 32px;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    position: sticky;
    top: 0;
    z-index: 1100;
    align-items: center;

    .smartScreen {
      cursor: pointer;
      color: #eee;
      font-size: 1.2em;
      font-weight: bold;
      background: linear-gradient(to right, #ff6ec4, #f9d423, #00c9ff, #92fe9d);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 0 2px rgba(255, 255, 255, 0.3);
    }

    .refresh {
      margin-right: 14px;
      font-size: 18px;
      font-weight: 600;
    }
  }
  .ant-layout-content {
    margin: 16px;
  }
  .ant-layout-footer {
    text-align: center;
    background-color: transparent;
    color: #4774a7;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.ant-layout-sider-collapsed & {
  .text {
    opacity: 0;
    width: 0;
    margin-left: 0;
  }
}

.site-layout .site-layout-background {
  background: #101830b3;
}
[data-theme='dark'] .site-layout .site-layout-background {
  background: #101830b3;
}

.ant-page-header {
  padding: 0px !important;
  margin-bottom: 12px;
}

.tabs-container {
  position: relative;
}

.context-menu-container {
  position: fixed;
  z-index: 10000;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  user-select: none;
}

.context-menu {
  margin: 0;
  border: none;
  box-shadow: none;
}

.context-menu .ant-menu-item {
  padding: 8px 16px;
  cursor: pointer;
}

.context-menu .ant-menu-item:hover {
  background-color: #f0f0f0;
}

/* 拖拽样式 */
:global(.dragging-tabs) {
  user-select: none;
}

:global(.tab-drag-ghost) {
  opacity: 0.4;
  background-color: #1890ff !important;
}

:global(.tab-drag-ghost .ant-tabs-tab-btn) {
  color: #fff !important;
}

:global(.tab-drag-chosen) {
  box-shadow: 0 0 8px rgba(24, 144, 255, 0.5);
  cursor: grabbing !important;
}

:global(.tab-drag-chosen .ant-tabs-tab-btn) {
  color: #1890ff;
}

:global(.tab-drag-drag) {
  opacity: 0.9;
  background-color: #1890ff !important;
  border-radius: 4px;
  cursor: grabbing !important;
}

:global(.tab-drag-drag .ant-tabs-tab-btn) {
  color: #fff !important;
}

:deep(.ant-tabs-tab) {
  cursor: grab;
  transition: all 0.2s ease;
}

:deep(.ant-tabs-tab:hover) {
  background-color: rgba(24, 144, 255, 0.1);
}

:deep(.ant-tabs-tab:active) {
  cursor: grabbing;
}
</style>
